import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ImageIcon } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { mediaUrl } from "@/lib/api";

const FALLBACK = [
  { id: "f1", title: "Dzinēja kapitālremonts", description: "Pilna dzinēja pārbūve ar jaunām blīvēm.", url: "https://images.unsplash.com/photo-1552656967-7a0991a13906?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { id: "f2", title: "Metināšanas darbi", description: "Virsbūves un rāmja metināšana ar argonu.", url: "https://images.unsplash.com/photo-1563229708-55d0a600b01d?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { id: "f3", title: "Bremžu sistēma", description: "Disku un kluču maiņa, suportu apkope.", url: "https://images.unsplash.com/photo-1696494561430-de087dd0bd69?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { id: "f4", title: "Servisa zāle", description: "Divi pacēlāji un dīleru diagnostika.", url: "https://images.pexels.com/photos/3807517/pexels-photo-3807517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { id: "f5", title: "Dzinēja nodalījums", description: "Rūpīga revīzija pirms remonta.", url: "https://images.unsplash.com/photo-1666554498255-5250121b4865?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { id: "f6", title: "Diagnostika", description: "Kļūdu nolasīšana un dzīvo datu analīze.", url: "https://images.pexels.com/photos/4116221/pexels-photo-4116221.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

export const GallerySection = ({ items }) => {
  const list = items.length > 0 ? items : FALLBACK;
  const [zoom, setZoom] = useState(null);

  return (
    <section id="galerija" className="relative border-t border-white/10 bg-[#0a0a0e] py-24 sm:py-32">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-10">
        <SectionLabel num="/ 04">Galerija</SectionLabel>
        <Reveal>
          <h2 className="mt-5 max-w-3xl font-head text-4xl font-extrabold uppercase italic leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl">
            <span className="metal-text">DARBI NO MŪSU</span> <span className="crimson-text">DARBNĪCAS</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
          {list.map((g, i) => (
            <Reveal key={g.id} delay={(i % 3) * 0.06}>
              <button
                onClick={() => setZoom(g)}
                data-testid={`gallery-item-${g.id}`}
                className="group block w-full text-left"
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden border border-white/10 bg-[#101015]">
                  <img
                    src={mediaUrl(g.url)}
                    alt={g.title || "Galerija"}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-[900ms] group-hover:scale-[1.05]"
                  />
                  <span className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center border border-white/20 bg-black/40 opacity-0 backdrop-blur transition-opacity duration-500 group-hover:opacity-100">
                    <ImageIcon className="h-3.5 w-3.5 text-white" />
                  </span>
                </div>
                {(g.title || g.description) && (
                  <div className="mt-3 border-l border-rose-600/60 pl-3">
                    <div className="font-head text-base font-bold uppercase leading-tight tracking-tight text-slate-100">
                      {g.title || g.description}
                    </div>
                  </div>
                )}
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {zoom && (
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/92 p-4 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setZoom(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center border border-white/20 text-white"
              onClick={() => setZoom(null)}
              data-testid="gallery-lightbox-close"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={mediaUrl(zoom.url)}
              alt={zoom.title}
              className="max-h-[88vh] w-auto max-w-[94vw] border border-white/10 object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
